import { useTranslation } from 'react-i18next';
import { CircularProgress, Tooltip } from '@mui/material';
import { transparentize } from 'polished';
import styled, { useTheme } from 'styled-components';

import { SUPPORTED_CURRENCIES } from '@/constants/common';
import { CHARACTER_LIMITS } from '@/features/canvas/constants/character-limits';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { InitiativeBudgetField } from '@/features/canvas/types/budget-type';
import { getDisplayText } from '@/features/canvas/utils/text-utils';

import { BudgetCircularProgress } from './BudgetCircularProgress';

// Extended type to handle both snake_case and camelCase properties
type ExtendedInitiativeBudgetField = InitiativeBudgetField & {
  displayTitle?: string;
  popupShow?: boolean;
  textValue?: string;
};

interface InitiativeBudgetTabProps {
  initiativeId: number;
}

export const InitiativeBudgetTab = ({ initiativeId }: InitiativeBudgetTabProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { allBudgets, popupVisible } = useEditorContext();
  const budget = allBudgets?.find(b => b.initiative_id === initiativeId);
  const isLoading = !allBudgets;

  // Debug the budget data as soon as we get it
  console.log('🔍 InitiativeBudgetTab - allBudgets:', allBudgets);
  console.log('🔍 InitiativeBudgetTab - initiativeId:', initiativeId);
  console.log('🔍 InitiativeBudgetTab - found budget:', budget);

  if (isLoading) {
    return (
      <LoaderWrapper>
        <StyledLoader />
      </LoaderWrapper>
    );
  }

  if (!budget?.fields) {
    return <LoaderWrapper>{t('editor.sidebar.initiative.budget.no_budget')}</LoaderWrapper>;
  }

  const currencySymbol =
    SUPPORTED_CURRENCIES.find(c => c.currency === budget.currency)?.symbol || '£';

  // Get field values directly from fields with proper typing
  const standardFields = budget.fields.filter((f: InitiativeBudgetField) =>
    ['total', 'capex', 'opex', 'spend'].includes(f.type)
  );
  // Log all budget fields first to see the structure
  console.log('🔍 InitiativeBudgetTab - ALL budget.fields:', budget.fields);

  const customFields = budget.fields.filter((f: ExtendedInitiativeBudgetField) => {
    if (!['big', 'small'].includes(f.type)) return false;

    // In both editor and public view, rely on the popup_show field
    // After the backend fix, this should be correctly set in snapshots
    return f.popup_show === true || f.popupShow === true;
  });

  const totalField = standardFields.find((f: InitiativeBudgetField) => f.type === 'total');
  const capexField = standardFields.find((f: InitiativeBudgetField) => f.type === 'capex');
  const opexField = standardFields.find((f: InitiativeBudgetField) => f.type === 'opex');
  const spendField = standardFields.find((f: InitiativeBudgetField) => f.type === 'spend');

  const totalBudget = totalField?.value || 0;
  const capex = capexField?.value || 0;
  const opex = opexField?.value || 0;
  const spend = spendField?.value || 0;
  const remaining = totalBudget - spend;
  const remainingPercentage = totalBudget > 0 ? (remaining / totalBudget) * 100 : 0;

  // Pie chart data for CapEx vs OpEx
  const total = capex + opex;
  const capexPercentage = total > 0 ? ((capex / total) * 100).toFixed(0) : '0';
  const opexPercentage = total > 0 ? ((opex / total) * 100).toFixed(0) : '0';

  // Calculate label positions for pie chart
  const opexAngle = total > 0 ? (opex / total) * 360 : 0;
  const opexLabelAngle = opexAngle / 2;
  const capexLabelAngle = opexAngle + (360 - opexAngle) / 2;

  const getLabelPosition = (angle: number, radius = 35) => {
    const radian = ((angle - 90) * Math.PI) / 180;
    return {
      x: 62.5 + radius * Math.cos(radian),
      y: 62.5 + radius * Math.sin(radian),
    };
  };

  const capexLabelPos = getLabelPosition(capexLabelAngle);
  const opexLabelPos = getLabelPosition(opexLabelAngle);

  const capexPercentageDisplay = `${capexPercentage}%`;
  const opexPercentageDisplay = `${opexPercentage}%`;
  const capexLabel = 'C';
  const opexLabel = 'O';

  // Check popupVisible for standard budget fields like Initiative fields do
  const shouldShowCapexOpex = popupVisible.some(
    f => (f.title === 'CapEx' && f.type === 'capex') || (f.title === 'OpEx' && f.type === 'opex')
  );

  return (
    <TabContent>
      <BudgetSection>
        <BudgetGrid>
          <BudgetCard>
            <SectionTitle>{t('editor.sidebar.initiative.budget.total_budget')}</SectionTitle>
            <BudgetValue>
              {currencySymbol}
              {totalBudget.toLocaleString()}
            </BudgetValue>
          </BudgetCard>

          {spendField && popupVisible.some(f => f.title === 'Spent' && f.type === 'spend') && (
            <SpendCard>
              <SpendLayout>
                <SpendValuesGrid>
                  <SectionTitle>{t('editor.sidebar.initiative.budget.expenditure')}</SectionTitle>
                  <SpendValueWrapper>
                    <SpendValue style={{ color: '#c92c2d' }}>
                      {currencySymbol}
                      {spend.toLocaleString()}
                    </SpendValue>
                    <SpendLabel>{t('editor.sidebar.initiative.budget.spent')}</SpendLabel>
                    <SpendValue style={{ color: remaining < 0 ? '#c92c2d' : '#43bf09' }}>
                      {remaining < 0 ? '-' : ''}
                      {currencySymbol}
                      {Math.abs(remaining).toLocaleString()}
                    </SpendValue>
                    <SpendLabel>{t('editor.sidebar.initiative.budget.remaining')}</SpendLabel>
                  </SpendValueWrapper>
                </SpendValuesGrid>
                <RadialChartWrapper>
                  <BudgetCircularProgress
                    percentage={remainingPercentage}
                    spent={spend}
                    total={totalBudget}
                    currencySymbol={currencySymbol}
                    isOverBudget={remaining < 0}
                  />
                </RadialChartWrapper>
              </SpendLayout>
            </SpendCard>
          )}

          {shouldShowCapexOpex && (
            <CapexOpexCard>
              <CapexOpexLayout>
                <CapexOpexValuesGrid>
                  <SectionTitle>{t('editor.sidebar.initiative.budget.capex_opex')}</SectionTitle>
                  <CapexOpexValueWrapper>
                    <CapexOpexValue>
                      {currencySymbol}
                      {capex.toLocaleString()}
                    </CapexOpexValue>
                    <CapexOpexLabel>{t('editor.sidebar.initiative.budget.capex')}</CapexOpexLabel>
                    <CapexOpexValue style={{ color: '#a5a5a5' }}>
                      {currencySymbol}
                      {opex.toLocaleString()}
                    </CapexOpexValue>
                    <CapexOpexLabel>{t('editor.sidebar.initiative.budget.opex')}</CapexOpexLabel>
                  </CapexOpexValueWrapper>
                </CapexOpexValuesGrid>
                {total > 0 && (
                  <PieChartWrapper>
                    <PieChart viewBox="0 0 125 125">
                      <circle
                        cx="62.5"
                        cy="62.5"
                        r="62.5"
                        fill={theme.palette.brand.editorTabPrimary}
                      />
                      <path
                        d={`M 62.5 62.5 L 62.5 0 A 62.5 62.5 0 ${opexAngle > 180 ? 1 : 0} 1 ${
                          62.5 + 62.5 * Math.sin((opexAngle * Math.PI) / 180)
                        } ${62.5 - 62.5 * Math.cos((opexAngle * Math.PI) / 180)} Z`}
                        fill="#a5a5a5"
                      />
                      {/* Percentage labels */}
                      <g>
                        <text
                          x={capexLabelPos.x}
                          y={capexLabelPos.y - 7}
                          fill="white"
                          fontSize="14"
                          fontWeight="600"
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          {capexPercentageDisplay}
                        </text>
                        <text
                          x={capexLabelPos.x}
                          y={capexLabelPos.y + 7}
                          fill="white"
                          fontSize="12"
                          fontWeight="600"
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          {capexLabel}
                        </text>
                      </g>
                      <g>
                        <text
                          x={opexLabelPos.x}
                          y={opexLabelPos.y - 7}
                          fill="white"
                          fontSize="14"
                          fontWeight="600"
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          {opexPercentageDisplay}
                        </text>
                        <text
                          x={opexLabelPos.x}
                          y={opexLabelPos.y + 7}
                          fill="white"
                          fontSize="12"
                          fontWeight="600"
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          {opexLabel}
                        </text>
                      </g>
                    </PieChart>
                  </PieChartWrapper>
                )}
              </CapexOpexLayout>
            </CapexOpexCard>
          )}
        </BudgetGrid>

        {/* Custom Fields Section */}
        {(() => {
          console.log('🔍 InitiativeBudgetTab - Custom fields found:', customFields.length);
          console.log('🔍 InitiativeBudgetTab - Custom fields data:', customFields);
          return null;
        })()}
        {customFields.length > 0 && (
          <CustomFieldsGrid>
            {customFields.map((customField: ExtendedInitiativeBudgetField) => {
              console.log('🔍 InitiativeBudgetTab - Rendering custom field:', {
                id: customField.id,
                display_title: customField.display_title,
                displayTitle: customField.displayTitle,
                text_value: customField.text_value,
                textValue: customField.textValue,
              });

              const fullTitle =
                customField.displayTitle ||
                customField.display_title ||
                customField.title ||
                'Untitled';

              const {
                displayText: titleDisplayText,
                tooltipText: titleTooltipText,
                needsTooltip,
              } = getDisplayText(fullTitle, CHARACTER_LIMITS.CUSTOM_FIELD_TITLE_DISPLAY);

              return (
                <CustomFieldCard key={customField.id}>
                  {needsTooltip ? (
                    <Tooltip title={titleTooltipText} placement="top">
                      <CustomFieldTitle>{titleDisplayText}</CustomFieldTitle>
                    </Tooltip>
                  ) : (
                    <CustomFieldTitle>{titleDisplayText}</CustomFieldTitle>
                  )}
                  <CustomFieldTextValue>
                    {customField.textValue || customField.text_value || '-'}
                  </CustomFieldTextValue>
                </CustomFieldCard>
              );
            })}
          </CustomFieldsGrid>
        )}
      </BudgetSection>
    </TabContent>
  );
};

const TabContent = styled.div`
  padding: 2rem;
  height: 100%;
  overflow-y: auto;
`;

const LoaderWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 1rem 0;
`;

const StyledLoader = styled(CircularProgress)`
  color: ${({ theme }) => theme.palette.brand.editorTabPrimary};
`;

const BudgetSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-bottom: 3rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.brand.textPrimary};
  margin-bottom: 6px;
`;

const BudgetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const BudgetCard = styled.div`
  background: ${({ theme }) => theme.palette.brand.corePPTWhite};
  border-radius: 1rem;
  padding: 1.5rem;
  -webkit-box-shadow: inset 0px 0px 3px -1px ${({ theme }) => transparentize(0.5, theme.palette.brand.black)};
  -moz-box-shadow: inset 0px 0px 3px -1px ${({ theme }) => transparentize(0.5, theme.palette.brand.black)};
  box-shadow: inset 0px 0px 3px -1px ${({ theme }) => transparentize(0.5, theme.palette.brand.black)};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SpendCard = styled(BudgetCard)``;

const CapexOpexCard = styled(BudgetCard)``;

const BudgetValue = styled.span<{ $isNegative?: boolean }>`
  font-size: 2.5rem;
  font-weight: 600;
  color: ${({ theme, $isNegative }) =>
    $isNegative ? '#f44336' : theme.palette.brand.editorTabPrimary};
`;

const SpendLayout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 2rem;
  justify-content: space-between;
`;

const SpendValuesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
  flex: 1;
`;

const SpendValueWrapper = styled.div`
  margin-left: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  & > :nth-child(2) {
    margin-bottom: 1rem;
  }
`;

const SpendValue = styled.span`
  font-size: 2rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.brand.editorTabPrimary};
`;

const SpendLabel = styled.span`
  font-size: 1.4rem;
  font-weight: 600;
  font-style: italic;
  color: ${({ theme }) => theme.palette.brand.textPrimary};
`;

const RadialChartWrapper = styled.div`
  flex-shrink: 0;
  height: 125px;
  width: 125px;
`;

const CapexOpexLayout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 2rem;
  justify-content: space-between;
`;

const CapexOpexValuesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
  flex: 1;
`;

const CapexOpexValueWrapper = styled.div`
  margin-left: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  & > :nth-child(2) {
    margin-bottom: 1rem;
  }
`;

const CapexOpexValue = styled.span`
  font-size: 1.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.brand.editorTabPrimary};
`;

const CapexOpexLabel = styled.span`
  font-size: 1.4rem;
  font-weight: 600;
  font-style: italic;
  color: ${({ theme }) => theme.palette.brand.textPrimary};
`;

const PieChartWrapper = styled.div`
  flex-shrink: 0;
`;

const PieChart = styled.svg`
  width: 125px;
  height: 125px;
`;

const CustomFieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr); // 6 columns for more granular control
  gap: 2rem;
`;

const CustomFieldCard = styled.div`
  background: ${({ theme }) => theme.palette.brand.corePPTWhite};
  border-radius: 1rem;
  padding: 1.5rem;
  -webkit-box-shadow: inset 0px 0px 3px -1px ${({ theme }) => transparentize(0.5, theme.palette.brand.black)};
  -moz-box-shadow: inset 0px 0px 3px -1px ${({ theme }) => transparentize(0.5, theme.palette.brand.black)};
  box-shadow: inset 0px 0px 3px -1px ${({ theme }) => transparentize(0.5, theme.palette.brand.black)};
  display: flex;
  flex-direction: column;
  gap: 1rem;
  grid-column: span 3; // Each takes 3 out of 6 columns
`;

const CustomFieldTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.brand.textPrimary};
  margin: 0;
`;

const CustomFieldTextValue = styled.p`
  color: #6e6e72;
  letter-spacing: 0.1px;
  line-height: 1.5;
  font-size: 1.4rem;
  word-break: break-word;
  margin: 0;
  white-space: pre-wrap;
  width: 100%;
  display: block;
  max-height: 120px;
  overflow-y: auto;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.palette.brand.backgroundDialog};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.palette.brand.textSecondary};
    border-radius: 3px;

    &:hover {
      background: ${({ theme }) => theme.palette.brand.textPrimary};
    }
  }
`;
